<template>
    <TailwindLayout title="Bridge">
        <div class="-mx -mt-10 space-y-6">
            <!-- Header band -->
            <div class="relative overflow-hidden rounded-2xl border border-rent-risk-blue-100 bg-gradient-to-br from-rent-risk-blue-100 via-white to-white px-6 py-7 sm:px-8">
                <div class="flex items-center gap-4">
                    <span class="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-rent-risk-blue-400 text-white shadow-sm">
                        <font-awesome-icon :icon="faAnchor" class="h-7 w-7" />
                    </span>
                    <div>
                        <h1 class="text-3xl font-bold tracking-tight text-rent-risk-blue-400">The Bridge</h1>
                        <p class="mt-1 text-sm text-gray-600">Internal staff tools. Everything here is gated and audited.</p>
                    </div>
                </div>
            </div>

            <!-- Support: the most-used entry point, kept at the top -->
            <div>
                <h2 class="mb-3 px-1 text-xs font-semibold uppercase tracking-wider text-gray-700">Support</h2>
                <Link
                    :href="route('impersonation.index')"
                    class="group flex flex-col gap-4 rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg sm:flex-row sm:items-center sm:justify-between"
                >
                    <div class="flex items-start gap-4">
                        <span class="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
                            <font-awesome-icon :icon="faUserAlt" class="h-6 w-6" />
                        </span>
                        <div>
                            <h3 class="text-base font-semibold text-gray-900">User Lookup &amp; Support Console</h3>
                            <p class="mt-1 max-w-2xl text-sm text-gray-600">
                                Find a user to open their support console — verification status, applications,
                                unblock actions, and impersonation.
                            </p>
                        </div>
                    </div>
                    <span class="inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-rent-risk-blue-400 px-4 py-2.5 text-sm font-semibold text-white transition group-hover:opacity-90 sm:self-auto">
                        Open Lookup
                        <font-awesome-icon :icon="faChevronRight" class="h-3.5 w-3.5" />
                    </span>
                </Link>
            </div>

            <!-- Tools -->
            <div>
                <h2 class="mb-3 px-1 text-xs font-semibold uppercase tracking-wider text-gray-700">Tools</h2>
                <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                    <Link
                        v-for="tool in tools"
                        :key="tool.name"
                        :href="tool.href"
                        class="group flex flex-col rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg"
                        :class="tool.accent"
                    >
                        <span class="inline-flex h-12 w-12 items-center justify-center rounded-xl" :class="tool.tile">
                            <font-awesome-icon :icon="tool.icon" class="h-6 w-6" />
                        </span>
                        <h3 class="mt-4 text-base font-semibold text-gray-900">{{ tool.name }}</h3>
                        <p class="mt-1 flex-1 text-sm text-gray-600">{{ tool.description }}</p>
                        <span class="mt-5 inline-flex items-center justify-between rounded-lg bg-rent-risk-blue-400 px-4 py-2.5 text-sm font-semibold text-white transition group-hover:opacity-90">
                            {{ tool.cta }}
                            <font-awesome-icon :icon="faChevronRight" class="h-3.5 w-3.5" />
                        </span>
                    </Link>
                </div>
            </div>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { Link } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import {
    faAnchor,
    faShieldExclamation,
    faClipboardList,
    faFileLines,
    faUserAlt,
    faChevronRight,
    faChartPie,
    faChartLine,
    faSquareDollar,
    faEnvelopeOpenText,
    faNetworkWired,
    faTags,
    faFileContract,
    faFileSignature,
    faUsers,
} from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'

const tools = [
    {
        name: 'Chargeback Disputes',
        description: 'Consolidated evidence for screening-fee charges.',
        href: route('bridge.disputes.index'),
        icon: faShieldExclamation,
        tile: 'bg-rose-100 text-rose-600',
        accent: 'border-t-4 border-t-rose-400',
        cta: 'Open Disputes',
    },
    {
        name: 'Enterprise Applications',
        description: 'Review Experian intake submissions and forwardable PDFs.',
        href: route('bridge.enterprise-applications.index'),
        icon: faClipboardList,
        tile: 'bg-amber-100 text-amber-600',
        accent: 'border-t-4 border-t-amber-400',
        cta: 'Open Applications',
    },
    {
        name: 'Email Logs',
        description: 'Delivery status and contents of every sent email.',
        href: route('email-logs.index'),
        icon: faFileLines,
        tile: 'bg-sky-100 text-sky-600',
        accent: 'border-t-4 border-t-sky-400',
        cta: 'Open Email Logs',
    },
    {
        name: 'Email Previews',
        description: 'Render every transactional email with placeholder data — no sending.',
        href: route('bridge.email-previews.index'),
        icon: faEnvelopeOpenText,
        tile: 'bg-fuchsia-100 text-fuchsia-600',
        accent: 'border-t-4 border-t-fuchsia-400',
        cta: 'Open Previews',
    },
    {
        name: 'Partner Payout Report',
        description: 'Affiliate payouts across all partners, with transfer status.',
        href: route('reports.partner-report'),
        icon: faSquareDollar,
        tile: 'bg-violet-100 text-violet-600',
        accent: 'border-t-4 border-t-violet-400',
        cta: 'Open Payouts',
    },
    {
        name: 'Reference Email Stats',
        description: 'Tenant-reference invite emails and conversion stats.',
        href: route('reference-email-stats.index'),
        icon: faChartPie,
        tile: 'bg-emerald-100 text-emerald-600',
        accent: 'border-t-4 border-t-emerald-400',
        cta: 'Open Stats',
    },
    {
        name: 'Campaign Attribution',
        description: 'Revenue per marketing email — clicks, conversions, and subscriptions.',
        href: route('bridge.campaign-attribution.index'),
        icon: faChartLine,
        tile: 'bg-teal-100 text-teal-600',
        accent: 'border-t-4 border-t-teal-400',
        cta: 'Open Report',
    },
    {
        name: 'Cloud IP Whitelist',
        description: 'Acknowledge Laravel Cloud outbound IPs as whitelisted with Experian.',
        href: route('bridge.cloud-ips.index'),
        icon: faNetworkWired,
        tile: 'bg-slate-100 text-slate-600',
        accent: 'border-t-4 border-t-slate-400',
        cta: 'Open Cloud IPs',
    },
    {
        name: 'Pricing Features',
        description: 'Manage the pricing-page feature rows and per-plan checkmarks, dashes, and text.',
        href: route('bridge.pricing-features.index'),
        icon: faTags,
        tile: 'bg-lime-100 text-lime-600',
        accent: 'border-t-4 border-t-lime-400',
        cta: 'Open Editor',
    },
    {
        name: 'RocketLawyer Templates',
        description: 'The lease, eviction, and form templates we have access to, and what each is for.',
        href: route('bridge.rocketlawyer-templates.index'),
        icon: faFileContract,
        tile: 'bg-indigo-100 text-indigo-600',
        accent: 'border-t-4 border-t-indigo-400',
        cta: 'Open Catalog',
    },
    {
        name: 'Lease Metrics',
        description: 'Leases generated this week, plus state, signing, and signer breakdowns.',
        href: route('bridge.lease-metrics.index'),
        icon: faFileSignature,
        tile: 'bg-cyan-100 text-cyan-600',
        accent: 'border-t-4 border-t-cyan-400',
        cta: 'Open Metrics',
    },
    {
        name: 'Onboarded Landlords',
        description: 'Plaid originator accounts and self-service resubmission flags for denied landlords.',
        href: route('onboarded-users.index'),
        icon: faUsers,
        tile: 'bg-indigo-100 text-indigo-600',
        accent: 'border-t-4 border-t-indigo-400',
        cta: 'Open Landlords',
    },
]
</script>
